import { noop } from 'lodash';
import { useCallback, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { CashflowDrawer } from '@/modules/cashflows/CashflowDrawer/CashflowDrawer';
import { ENTITY_TYPES } from '@/modules/entities/entities.constants';
import { CreateEntityShortFormModal } from '@/modules/entities/EntityShortFormModal/CreateEntityShortFormModal';
import { LogNewGiftModal } from '@/modules/gifting/LogNewGiftModal/LogNewGiftModal';
import { RecordExemptionUsedModal } from '@/modules/gifting/RecordExemptionUsedModal/RecordExemptionUsedModal';
import {
  GrowthProfileEditorScope,
  growthProfileEditorTarget,
  GrowthProfileModal,
} from '@/modules/growthProfiles/GrowthProfileModal/GrowthProfileModal';
import { Operations as GrowthProfilesTableOperations } from '@/modules/growthProfiles/GrowthProfilesTrowser/GrowthProfilesTable/graphql/GrowthProfilesTable.generated';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { CreateLiabilityModal } from '@/modules/liabilities/LiabilityModal/CreateLiabilityModal';
import { useTrowser } from '@/modules/navigation/trowsers/trowser.context';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';
import { UnreachableError } from '@/utils/errors';

export function ClientCashflowsPageAddButton() {
  const { householdId } = useHouseholdDetailsContext();
  const canEdit = useCanEditHouseholdData();

  const [isAddEntityModalOpen, setIsAddEntityModalOpen] = useState(false);
  const [isAddLiabilityModalOpen, setIsAddLiabilityModalOpen] = useState(false);
  const [isAddCashflowDrawerOpen, setIsAddCashflowDrawerOpen] = useState(false);
  const [isAddGiftModalOpen, setIsAddGiftModalOpen] = useState(false);
  const [isAddExemptionModalOpen, setIsAddExemptionModalOpen] = useState(false);
  // This menu only ever creates a profile, so one target covers it.
  const growthProfileEditorTrowser = useTrowser(TrowserKey.GrowthProfile);
  const addGrowthProfileTarget = growthProfileEditorTarget(
    GrowthProfileEditorScope.CashflowsAddMenu
  );
  const handleMenuClick = useCallback(
    (
      kind:
        | 'entity'
        | 'liability'
        | 'cashflow'
        | 'gift'
        | 'exemption'
        | 'growthProfile'
    ) => {
      // make sure we close all modals before opening a new one; this *should* never happen,
      // but just in case
      setIsAddEntityModalOpen(false);
      setIsAddLiabilityModalOpen(false);
      setIsAddCashflowDrawerOpen(false);
      setIsAddGiftModalOpen(false);
      setIsAddExemptionModalOpen(false);

      switch (kind) {
        case 'entity':
          setIsAddEntityModalOpen(true);
          break;
        case 'liability':
          setIsAddLiabilityModalOpen(true);
          break;
        case 'cashflow':
          setIsAddCashflowDrawerOpen(true);
          break;
        case 'gift':
          setIsAddGiftModalOpen(true);
          break;
        case 'exemption':
          setIsAddExemptionModalOpen(true);
          break;
        case 'growthProfile':
          growthProfileEditorTrowser.open({
            target: addGrowthProfileTarget,
          });
          break;
        default:
          throw new UnreachableError({
            case: kind,
            message: `Unreachable case in ClientWealthPageAddButton: ${kind}`,
          });
      }
    },
    [addGrowthProfileTarget, growthProfileEditorTrowser]
  );

  if (!householdId || !canEdit) return null;

  return (
    <>
      {/* always unmount/remount to clear state */}
      {isAddEntityModalOpen && (
        <CreateEntityShortFormModal
          isOpen={isAddEntityModalOpen}
          onClose={() => setIsAddEntityModalOpen(false)}
          // override the default "navigate to entity" behavior and just stay on this page
          onAfterCreate={noop}
          initialEntityType={ENTITY_TYPES.IRREVOCABLE_TRUST}
          householdId={householdId}
        />
      )}
      <CreateLiabilityModal
        isOpen={isAddLiabilityModalOpen}
        onClose={() => setIsAddLiabilityModalOpen(false)}
      />
      <CashflowDrawer
        cashflowId={null} // we only create cashflows from here, no edits
        isOpen={isAddCashflowDrawerOpen}
        onClose={() => setIsAddCashflowDrawerOpen(false)}
      />
      <LogNewGiftModal
        householdId={householdId}
        isOpen={isAddGiftModalOpen}
        onClose={() => setIsAddGiftModalOpen(false)}
      />
      <RecordExemptionUsedModal
        householdId={householdId}
        isOpen={isAddExemptionModalOpen}
        onClose={() => setIsAddExemptionModalOpen(false)}
      />
      {growthProfileEditorTrowser.target === addGrowthProfileTarget && (
        <GrowthProfileModal
          householdId={householdId}
          onClose={growthProfileEditorTrowser.close}
          refetchQueries={[
            GrowthProfilesTableOperations.Query.GrowthProfilesTable,
          ]}
        />
      )}
      <Button
        variant="primary"
        size="sm"
        startIcon={PlusIcon}
        onClick={() => handleMenuClick('cashflow')}
      >
        Add cash flow
      </Button>
    </>
  );
}
